import type {
  GenerationRequestStatus,
  GenerationRequestSummaryRead,
  PlannedPostRead,
} from '../../types/api';

/** What Home asks the list for: work stopped and waiting on a person. */
export const NEEDS_YOU_STATUSES: readonly GenerationRequestStatus[] = [
  'awaiting_review',
  'awaiting_final_review',
  'failed',
];

/**
 * One read covers the count and the rows. The heading counts what came back,
 * and says "50+" when the page was full rather than pretending to know more.
 */
export const NEEDS_YOU_PAGE_SIZE = 50;

/** Rows in view on Home; the rest of the page scrolls inside the list. */
export const NEEDS_YOU_VISIBLE = 3;

/** A failure older than this is history, not something waiting on you. */
const FAILED_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

export type NeedsYouItem =
  | { kind: 'request'; id: string; at: number; request: GenerationRequestSummaryRead }
  | { kind: 'plan'; id: string; at: number; plan: PlannedPostRead };

function time(value: string): number {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Runs waiting on review, recent failures, and calendar plans that parked —
 * newest first, one row per thing.
 *
 * A plan that parked at review made a request that is waiting too; the request
 * row stands for both, so the plan is dropped when its request is already on
 * the list. A deploy without the status filter answers with every state, so
 * the statuses are checked here again.
 */
export function needsYouItems(
  requests: readonly GenerationRequestSummaryRead[],
  plans: readonly PlannedPostRead[],
  now: number = Date.now(),
): NeedsYouItem[] {
  const items: NeedsYouItem[] = [];
  const requestIds = new Set<string>();

  for (const request of requests) {
    if (!NEEDS_YOU_STATUSES.includes(request.status)) continue;
    requestIds.add(request.id);
    const at = time(request.created_at);
    if (request.status === 'failed' && now - at > FAILED_WINDOW_MS) continue;
    items.push({ kind: 'request', id: request.id, at, request });
  }

  for (const plan of plans) {
    if (plan.automation_status !== 'needs_review' && plan.automation_status !== 'failed') continue;
    if (plan.generation_request_id && requestIds.has(plan.generation_request_id)) continue;
    items.push({ kind: 'plan', id: plan.id, at: time(plan.updated_at), plan });
  }

  return items.sort((a, b) => b.at - a.at);
}
